import { useState, useEffect } from 'react'
import { useSignUp } from '@clerk/clerk-react'
import { useNavigate, Link } from 'react-router-dom'
import { ChevronLeft, ArrowRight, Loader2, Eye, EyeOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { BrainLogo } from '@/components/ui/brain-logo'

export default function SignUpPage() {
  const { signUp, setActive, isLoaded } = useSignUp()
  const navigate = useNavigate()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isEmailLoading, setIsEmailLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pendingVerification, setPendingVerification] = useState(false)
  const [code, setCode] = useState('')

  // Reset loading state when component mounts or when user navigates back
  useEffect(() => {
    // Reset loading state on mount
    setIsGoogleLoading(false)
    setIsEmailLoading(false)

    // Handle browser back button - reset loading state when page becomes visible again
    const handlePageShow = (e: PageTransitionEvent) => {
      // If the page was loaded from cache (back/forward navigation), reset loading states
      if (e.persisted) {
        setIsGoogleLoading(false)
        setIsEmailLoading(false)
      }
    }

    // Listen for pageshow event (fires when page is shown, including back navigation)
    window.addEventListener('pageshow', handlePageShow)

    // Cleanup
    return () => {
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isLoaded || !signUp) return

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match. Please try again.')
      return
    }

    setIsEmailLoading(true)
    setError(null)

    try {
      const result = await signUp.create({
        emailAddress: email,
        password,
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
      })

      // Check if email verification is required
      if (result.status === 'missing_requirements') {
        await signUp.prepareEmailAddressVerification({ strategy: 'email_code' })
        setPendingVerification(true)
      } else if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId })
        navigate('/onboarding')
      }
    } catch (err: any) {
      const errorMessage = err.errors?.[0]?.message || 'Failed to create account. Please try again.'
      if (!errorMessage.toLowerCase().includes('data breach')) {
        setError(errorMessage)
      }
    } finally {
      setIsEmailLoading(false)
    }
  }

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isLoaded || !signUp) return

    setIsEmailLoading(true)
    setError(null)

    try {
      const result = await signUp.attemptEmailAddressVerification({
        code,
      })

      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId })
        navigate('/onboarding')
      } else {
        setError('Verification failed. Please try again.')
      }
    } catch (err: any) {
      setError(err.errors?.[0]?.message || 'Invalid verification code.')
    } finally {
      setIsEmailLoading(false)
    }
  }

  const handleOAuth = async (strategy: 'oauth_google') => {
    if (!isLoaded || !signUp) {
      setError('Authentication service is not ready. Please try again.')
      return
    }

    setError(null)
    setIsGoogleLoading(true)

    try {
      const baseUrl = window.location.origin
      await signUp.authenticateWithRedirect({
        strategy,
        redirectUrl: `${baseUrl}/sso-callback`,
        redirectUrlComplete: `${baseUrl}/onboarding`,
      })
    } catch (err: any) {
      console.error('OAuth error:', err)
      setError(err.errors?.[0]?.message || `Failed to sign up with Google. Please try again.`)
      setIsGoogleLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Section - Branding */}
      <div className="hidden lg:flex fixed left-0 top-0 h-screen w-[504px] bg-[#2b42a3] overflow-hidden justify-center">
        <div className="absolute inset-0 bg-[#414fbf] opacity-90" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div>
            <div className="mb-8">
              <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm">
                <ChevronLeft className="h-4 w-4" />
                <span>Return Home</span>
              </Link>
            </div>
            <div className="mb-8">
              <BrainLogo size="lg" variant="dark" showGlow={false} />
            </div>
            <div className="mb-6">
              <h1 className="text-[39px] font-black leading-[47px] mb-4">
                Join the AI Expert Community
              </h1>
              <p className="text-[#bdbdbd] text-base leading-[28px]">
                Create your account to access your workspace, collaborate seamlessly, and start building with us.
              </p>
            </div>
          </div>
        </div>
        <img
          src="/sign_in_image.png"
          alt="Sign in illustration"
          className="absolute bottom-0 left-0 right-0 w-full h-auto object-contain"
        />
      </div>

      {/* Right Section - Form */}
      <div className="flex-1 lg:ml-[504px] flex flex-col bg-white">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between p-4 border-b">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm">
            <ChevronLeft className="h-4 w-4" />
            <span>Return Home</span>
          </Link>
          <Link to="/sign-in" className="text-sm text-slate-600">
            <span className="font-normal">Already a Member? </span>
            <span className="font-bold underline">LOG IN NOW</span>
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-center p-8 lg:p-12 bg-[#F8FAFC]">
          <div className="w-full max-w-[460px]">
            <div className="border border-slate-200 rounded-[40px] p-6">
              <div className="rounded-[40px] p-6 -m-1">
                {/* Header */}
                <div className="mb-8">
                  <p className="text-[12.8px] font-normal text-slate-500 mb-2">LET&apos;S GET YOU STARTED</p>
                  <h2 className="text-[25px] font-medium leading-[44px]">Create an Account</h2>
                </div>

                {!pendingVerification ? (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* First Name */}
                    <div className="space-y-3">
                      <Label htmlFor="signup-firstname" className="text-base text-[#303030]">
                        First Name<span className="text-red-500"> *</span>
                      </Label>
                      <Input
                        id="signup-firstname"
                        type="text"
                        maxLength={25}
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="John"
                        required
                        className="h-[57px] rounded-[27px] border-2 border-slate-200 px-6 text-base"
                      />
                    </div>

                    {/* Last Name */}
                    <div className="space-y-3">
                      <Label htmlFor="signup-lastname" className="text-base text-[#303030]">
                        Last Name<span className="text-red-500"> *</span>
                      </Label>
                      <Input
                        id="signup-lastname"
                        type="text"
                        maxLength={25}
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Doe"
                        required
                        className="h-[57px] rounded-[27px] border-2 border-slate-200 px-6 text-base"
                      />
                    </div>

                    {/* Email */}
                    <div className="space-y-3">
                      <Label htmlFor="signup-email" className="text-base text-[#303030]">
                        Email Address<span className="text-red-500"> *</span>
                      </Label>
                      <Input
                        id="signup-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="johndoe@gmail.com"
                        required
                        className="h-[57px] rounded-[27px] border-2 border-slate-200 px-6 text-base"
                      />
                    </div>

                    {/* Password */}
                    <div className="space-y-3">
                      <Label htmlFor="signup-password" className="text-base text-[#303030]">
                        Password<span className="text-red-500"> *</span>
                      </Label>
                      <div className="relative">
                        <Input
                          id="signup-password"
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your password"
                          required
                          minLength={8}
                          className="h-[57px] rounded-[27px] border-2 border-slate-200 px-6 pr-12 text-base"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 focus:outline-none transition-colors"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Confirm Password */}
                    <div className="space-y-3">
                      <Label htmlFor="signup-confirm-password" className="text-base text-[#303030]">
                        Confirm Password<span className="text-red-500"> *</span>
                      </Label>
                      <div className="relative">
                        <Input
                          id="signup-confirm-password"
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Confirm your password"
                          required
                          minLength={8}
                          className="h-[57px] rounded-[27px] border-2 border-slate-200 px-6 pr-12 text-base"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 focus:outline-none transition-colors"
                          aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                      <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                        {error}
                      </div>
                    )}

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={isEmailLoading}
                      className="w-full h-[54px] rounded-[50px] bg-[#4669ed] hover:bg-[#2b42a3] text-white font-bold text-base mt-6"
                    >
                      {isEmailLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Creating account...
                        </>
                      ) : (
                        <>
                          Get Started
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleVerifyCode} className="space-y-5">
                    <div className="space-y-3">
                      <Label htmlFor="verification-code" className="text-base text-[#303030]">
                        Verification Code<span className="text-red-500"> *</span>
                      </Label>
                      <Input
                        id="verification-code"
                        type="text"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder="Enter verification code"
                        required
                        className="h-[57px] rounded-[27px] border-2 border-slate-200 px-6 text-base"
                      />
                      <p className="text-sm text-slate-500">
                        We&apos;ve sent a verification code to {email}
                      </p>
                    </div>

                    {error && (
                      <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                        {error}
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={isEmailLoading}
                      className="w-full h-[54px] rounded-[50px] bg-[#4669ed] hover:bg-[#2b42a3] text-white font-bold text-base"
                    >
                      {isEmailLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          Verify Code
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </form>
                )}

                {/* Divider */}
                {!pendingVerification && (
                  <>
                    <div className="relative my-8">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-slate-200" />
                      </div>
                      <div className="relative flex justify-center">
                        <span className="bg-[#F8FAFC] px-4 text-[12.8px] font-bold text-[#212121]">Or</span>
                      </div>
                    </div>

                    {/* Social Login Buttons */}
                    <div className="space-y-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleOAuth('oauth_google')}
                        disabled={isGoogleLoading || !isLoaded || pendingVerification}
                        className="w-full h-12 rounded-[27px] border border-[#eeeeee] bg-neutral-50 hover:bg-neutral-100 text-[#616161] text-[12.8px] font-normal justify-start gap-3"
                      >
                        {isGoogleLoading ? (
                          <>
                            <Loader2 className="w-[18px] h-[18px] animate-spin" />
                            Connecting...
                          </>
                        ) : (
                          <>
                            <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24">
                              <path
                                fill="#4285F4"
                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                              />
                              <path
                                fill="#34A853"
                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                              />
                              <path
                                fill="#FBBC05"
                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                              />
                              <path
                                fill="#EA4335"
                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                              />
                            </svg>
                            Sign up with Google
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Sign In Link */}
                    <p className="text-center text-[12.8px] text-[#212121] mt-6">
                      Already have an account?{' '}
                      <Link to="/sign-in" className="font-bold underline">
                        LOGIN HERE
                      </Link>
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

